import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'
import type {ComponentType} from 'react'

import {buildIssueNewChooseRoute} from './issue-new-choose-route'
import {buildIssueViewerRoute} from './issue-viewer-route'
import {buildIssuesLayoutRoute} from './issues-layout-route'
import {buildIssueNewRoute} from './issue-new-route'
import {
  buildIssuesIndexContentRoute,
  buildIssuesLabelContentRoute,
  ISSUES_INDEX_PATHS,
} from './issues-index-content-route'
import {buildLabelsRoute} from './labels-route'
import {labelsLegacyRedirectLoader} from './labels-legacy-redirect-loader'
import {buildMilestoneEditRoute} from './milestone-edit-route'
import {buildMilestoneNewRoute} from './milestone-new-route'
import {buildMilestoneIndexRoute} from './milestone-index-route'
import {buildMilestoneShowRoute} from './milestone-show-route'
import {buildRepositoryProjectsRoute} from './repository-projects-route'
import {buildRepositoryProjectTemplatesRoute} from './repository-project-templates-route'
import {buildSavedViewNewRoute, buildSavedViewRoute, buildSavedViewsRoute} from './saved-views-routes'
import {withIssuesLayoutContext} from './with-issues-layout-context'

export interface IssuesRouteComponents {
  IssueNewChoose: ComponentType
  IssueViewer: ComponentType
  IssuesIndexContent: ComponentType
  IssuesLayout: ComponentType
  IssueNew: ComponentType
  Labels: ComponentType
  MilestoneEdit: ComponentType
  MilestoneNew: ComponentType
  MilestoneIndex: ComponentType
  MilestoneShow: ComponentType
  RepositoryProjectsPage: ComponentType
  SavedView: ComponentType
  SavedViewNew: ComponentType
  SavedViews: ComponentType
}

// The loader always redirects, so nothing is ever shown here. But React Router still
// renders the matched route and warns if a leaf route has no Component. this
// fails the tests (vitest-fail-on-console), so we pass an empty component
const LabelsRedirectFallback = () => null

// React Router requires a child match for URLs below /issues. These no-op routes provide that match
// while IssuesIndexContent renders the page
// Inspired by packages/pull-requests/routes/create-pull-requests-routes.tsx
function IssuesIndexCleanPathRoute() {
  return null
}

const issuesIndexCleanPathRoutes: RouteObject[] = [
  {id: 'issuesIndexRoute', path: ISSUES_INDEX_PATHS.index, index: true, Component: IssuesIndexCleanPathRoute},
  {
    id: 'issuesIndexMentionedRoute',
    path: ISSUES_INDEX_PATHS.mentioned,
    Component: IssuesIndexCleanPathRoute,
  },
  {
    id: 'issuesIndexAssignedRoute',
    path: ISSUES_INDEX_PATHS.assigned,
    Component: IssuesIndexCleanPathRoute,
  },
  {
    id: 'issuesIndexCreatedByRoute',
    path: ISSUES_INDEX_PATHS.createdBy,
    Component: IssuesIndexCleanPathRoute,
  },
  {
    id: 'issuesIndexCreatedByAppRoute',
    path: ISSUES_INDEX_PATHS.createdByApp,
    Component: IssuesIndexCleanPathRoute,
  },
]

function pathlessLayout(route: RouteObject): RouteObject {
  return {...route, path: undefined}
}

/**
 * Builds the Issues route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `issues`-bound singletons directly.
 */
export function createIssuesRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  components: IssuesRouteComponents,
): RouteObject[] {
  const {IssuesLayout} = components
  // Each page is wrapped here, not in `IssuesLayout`: wrapping the layout makes saved views load too early.
  const IssueNewChoose = withIssuesLayoutContext(components.IssueNewChoose)
  const IssueViewer = withIssuesLayoutContext(components.IssueViewer)
  const IssuesIndexContent = withIssuesLayoutContext(components.IssuesIndexContent)
  const IssueNew = withIssuesLayoutContext(components.IssueNew)
  const Labels = withIssuesLayoutContext(components.Labels)
  const MilestoneEdit = withIssuesLayoutContext(components.MilestoneEdit)
  const MilestoneNew = withIssuesLayoutContext(components.MilestoneNew)
  const MilestoneIndex = withIssuesLayoutContext(components.MilestoneIndex)
  const MilestoneShow = withIssuesLayoutContext(components.MilestoneShow)
  const RepositoryProjectsPage = withIssuesLayoutContext(components.RepositoryProjectsPage)
  const SavedView = withIssuesLayoutContext(components.SavedView)
  const SavedViewNew = withIssuesLayoutContext(components.SavedViewNew)
  const SavedViews = withIssuesLayoutContext(components.SavedViews)
  // Keep the query route's typed path for its loader and consumers, but make the router layout
  // pathless so it cannot terminate at the bare repository URL without an Issues child match.
  const issuesLayoutRoute = pathlessLayout(
    buildIssuesLayoutRoute(appBuilder).toRoute({
      Component: IssuesLayout,
      children: [
        buildLabelsRoute(appBuilder).toRoute({Component: Labels}),
        buildIssuesIndexContentRoute(appBuilder).toRoute({
          Component: IssuesIndexContent,
          children: issuesIndexCleanPathRoutes,
        }),
        buildIssuesLabelContentRoute(appBuilder).toRoute({Component: IssuesIndexContent}),
        buildSavedViewsRoute(appBuilder).toRoute({Component: SavedViews}),
        buildSavedViewNewRoute(appBuilder).toRoute({Component: SavedViewNew}),
        buildSavedViewRoute(appBuilder).toRoute({Component: SavedView}),
        buildIssueNewRoute(appBuilder).toRoute({Component: IssueNew}),
        buildIssueNewChooseRoute(appBuilder).toRoute({Component: IssueNewChoose}),
        buildIssueViewerRoute(appBuilder).toRoute({
          Component: IssueViewer,
          handle: {canNavigateWithoutChild: true},
          // Artifact deep links reuse the mounted issue viewer; only this explicit suffix is accepted.
          children: [{id: 'issueViewerArtifactRoute', path: 'artifacts/:artifactNumber', Component: () => null}],
        }),
        buildMilestoneNewRoute(appBuilder).toRoute({Component: MilestoneNew}),
        buildMilestoneEditRoute(appBuilder).toRoute({Component: MilestoneEdit}),
        buildMilestoneIndexRoute(appBuilder).toRoute({Component: MilestoneIndex}),
        buildMilestoneShowRoute(appBuilder).toRoute({Component: MilestoneShow}),
        // Projects shares the Issues layout but has its own nav tab. In the repo app, this `selectedLink`
        // overrides the Issues default, so the Projects tab is highlighted.
        buildRepositoryProjectsRoute(appBuilder).toRoute({
          Component: RepositoryProjectsPage,
          handle: {selectedLink: 'repo_projects'},
        }),
        buildRepositoryProjectTemplatesRoute(appBuilder).toRoute({
          Component: RepositoryProjectsPage,
          handle: {selectedLink: 'repo_projects'},
        }),
        // defining it as a plain object because its just a legacy-redirect path
        {path: '/:owner/:repo/issues/labels', loader: labelsLegacyRedirectLoader, Component: LabelsRedirectFallback},
      ],
    }),
  )

  return [issuesLayoutRoute]
}
